import React from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";
import CardCommon from "../../../../components/card/CardCommon";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    tableContainer: {
      overflowX: "auto",
      overflowY: "hidden",
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px",
    },
    headerCell: {
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    stickyYearCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    bodyRow: {
      "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.background.paper,
      },
      "&:nth-of-type(even)": {
        backgroundColor: theme.palette.background.entity_background,
      },
    },
  }),
);

interface MonthlySalesTableProps {
  dummyData: any[];
  valueTotal: number[];
}

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ChartSalesTable: React.FC<MonthlySalesTableProps> = ({
  dummyData,
  valueTotal,
}) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();

  // Prepare table-friendly structure
  const rows = dummyData.map((row) => ({
    year: row.year ?? "Unknown",
    data:
      row.data?.map((monthData: any, idx: number) => ({
        month: monthData?.month ?? monthNames[idx],
        count: monthData?.count ?? 0,
        value: monthData?.value ?? 0,
      })) ?? monthNames.map((month) => ({ month, count: 0, value: 0 })),
  }));

  // Color helpers
  const getColor = (value: number, list: number[]) => {
    if (value === 0) return theme.palette.background.entity_background;
    const startColor = { r: 222, g: 255, b: 235 };
    const endColor = { r: 2, g: 105, b: 87 };
    const index = list.indexOf(value);
    const maxIndex = list.length - 1;
    const ratio = maxIndex === 0 ? 0 : index / maxIndex;
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;

    if (color.startsWith("rgb")) {
      const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      } else {
        return false;
      }
    } else if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        return false;
      }
    } else {
      return false;
    }

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance < 128;
  };
  return (
    <Table className={classes.table} size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell
             className={classes.stickyYearCell}
          >
            Year
          </TableCell>
          {rows[0]?.data.map((month: any, idx: number) => (
            <TableCell key={idx} align="center" className={classes.headerCell}>
              {month.month}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell className={classes.stickyYearCell}>{row.year}</TableCell>
            {row.data.map((cell: any, j: number) => {
              const bgColor = getColor(cell.value, valueTotal);
              return (
                <TableCell
                  key={j}
                  align="center"
                  style={{
                    borderBottom: "none",
                    width: `${100 / rows[0]?.data.length}%`,
                    padding: "0px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: bgColor,
                      borderRadius: "10px",
                      height: "42px",
                    }}
                  >
                  <div>
                       <Typography
                      variant="caption"
                      style={{
                        color: isDarkColor(bgColor) ? "white" : "black",
                        fontWeight: "bold",
                      }}
                    >
                      {cell.value === 0 ? "-" : cell.value.toFixed(2)}
                    </Typography>
                  </div>
                    <div>
                      <Typography
                      variant="caption"
                      style={{
                        color: isDarkColor(bgColor) ? "white" : "black",
                      }}
                    >
                      {cell.count}
                    </Typography>
                    </div>
                   
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ChartSalesTable;
